/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect } from 'react';

// Mantine
import { useForm } from '@mantine/form';

// Components
import {
	Button,
	PasswordInput,
	Text,
	TextInput,
	Title,
} from '@components/v2/ui';

// Api
import { useLogin } from '@features/auth/api/login';

// I18n
import { useTranslation } from 'react-i18next';

// Constants
import { REGEX_EMAIL } from '@config/constants/regex';

// React router
import { useBreakpoint } from '@hooks/useBreakpoint';
import { useSearchParams } from 'react-router';
import { FormWrapper } from '../../../../components/v2/ui/public/FormWrapper';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const SignIn = (props: {
	handlenavigate: (_target: string) => void;
}) => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();
	const { isMobile } = useBreakpoint();

	const form = useForm({
		mode: 'uncontrolled',
		initialValues: {
			email: '',
			password: '',
		},
		validate: {
			email: (value) =>
				REGEX_EMAIL.test(value.trim())
					? null
					: t('form.error.invalidEmail'),
			password: (value) =>
				value.length < 8
					? t('form.error.passwordMustHaveEightLetters')
					: null,
		},
	});

	const [searchParams] = useSearchParams();

	const login = useLogin();

	useEffect(() => {
		const emailParam = searchParams.get('email');
		if (emailParam) {
			form.setFieldValue('email', emailParam);
		}
	}, [searchParams]);

	/* ******************************** Helpers ******************************* */

	const handleForgotPassword = () => {
		props.handlenavigate('reset');
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<form
			onSubmit={form.onSubmit((values) => {
				login.mutate(values);
			})}
		>
			<FormWrapper>
				<Title
					ta="center"
					c={'var(--mantine-color-igluPrimary-9)'}
					order={isMobile ? 6 : 5}
				>
					{t('auth.connectToYourAccount')}
				</Title>
				<Text
					ta="center"
					c={'var(--mantine-color-igluTextSecondary-9)'}
					variant={isMobile ? 'body2' : 'body1'}
				>
					{t('auth.offsetYourCarbonFootprint')}
				</Text>
				<TextInput
					autoComplete="new-password"
					mt={'24px'}
					size="md"
					label={t('global.email')}
					autoFocus
					placeholder={t('auth.yourEmailAddress')}
					{...form.getInputProps('email')}
				/>
				<PasswordInput
					autoComplete="new-password"
					label={t('global.password')}
					mt={'6px'}
					placeholder={t('auth.yourPassword')}
					{...form.getInputProps('password')}
				/>
				<Button
					mt={'24px'}
					fullWidth
					variant={'primary'}
					loading={login && login.isPending}
					type="submit"
				>
					{t('auth.login')}
				</Button>
				<Button
					fullWidth
					variant={'transparent'}
					onClick={handleForgotPassword}
				>
					{t('auth.forgotPassword')}
				</Button>
			</FormWrapper>
		</form>
	);
};
